import { useId, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Icon, IconButton, ShowHide, Text, TextButton, Toast, Tooltip, useToast } from '@zillow/constellation-10';
import {
  IconErrorFilled,
  IconFloorPlanFilled,
  IconPanorama360Filled,
  IconPhotosFilled,
} from '@zillow/constellation-icons';

import { logError } from '@zg-rentals/log-error';
import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertyFloorPlanDeleteV2WebApiHandler_delete,
  useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_active,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { typeCheckUtils } from '@zg-rentals/zillow-rental-manager/utils';

import { setUnitField } from '../../../../../../hooks/queries/listingDetailsCache';
import { useGetListingDetails, useGetTours } from '../../../../../../hooks/queries/properties';
import { useDeleteFeedFloorPlan } from '../../../../../../hooks/useDeleteFeedFloorPlan';
import { useFeedOverrideSectionEnabled } from '../../../../../../hooks/useFeedOverrideSectionEnabled';
import { usePropertyEditMutation } from '../../../../../../hooks/usePropertyEditMutation';
import { getIsFeedFloorPlanDeletable } from '../../../../../../utils/feedFloorPlanUtils';
import propertyUtils from '../../../../../../utils/propertyUtils';
import stringHelper from '../../../../../../utils/stringHelper';
import { getIsInputFormDisabled } from '../../../../utils';

import CreateUnitModal from '../../../../../modals/CreateUnitModal';
import FloorPlanModal from '../../../../../modals/FloorPlanModal';
import UnitModal from '../../../../../modals/UnitModal';

import { useAuth } from '../../../../../AuthProvider';
import UnitCard from '../UnitCard';

import type { ToastRootPropsInterface } from '@zillow/constellation-10';
import type { GetUserPropertyDetailsFloorPlanWebDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { DatarouterError } from '@zg-rentals/web-base';

type Props = {
  floorPlan: Partial<GetUserPropertyDetailsFloorPlanWebDto>;
  floorPlanIndex?: number;
};

const SavedFloorPlanCard = ({ floorPlan, floorPlanIndex }: Props) => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { floorplans: floorplansWithTours } = useGetTours(listingAlias, listingDetails);

  const floorplans = listingDetails?.floorPlans || [];
  const floorplanHasTours =
    floorPlanIndex !== undefined
      ? floorplansWithTours[floorPlanIndex]?.units?.some((unit) => unit.vrModel?.tourUrl || unit.externalTour?.tourUrl)
      : false;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;
  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);
  const isPartialListingManaged = listingDetails?.partialListing?.isPartialListingManaged ?? false;
  const hideAddButton = isInputFormDisabled || isPartialListingManaged;

  // Floor plans created in Rental Manager on a feed listing can be removed through their feed override; floor plans
  // provided by the feed stay read-only.
  const isFeed = listingDetails?.isFeed ?? false;
  const isFloorPlansFeedOverrideEnabled = useFeedOverrideSectionEnabled(listingAlias, FeedOverrideSection.FLOOR_PLANS);
  const isFeedFloorPlanDeletable = getIsFeedFloorPlanDeletable({
    isFeed,
    isFeedOverride: floorPlan.isFeedOverride,
    feedModelId: floorPlan.feedModelId,
    isFeedOverrideSectionEnabled: isFloorPlansFeedOverrideEnabled,
  });
  const isDeleteHidden = getIsInputFormDisabled(listingDetails, canUpdateListing, {
    isFeedOverrideEnabled: isFeedFloorPlanDeletable,
  });

  const [displayFloorPlanModal, setDisplayFloorPlanModal] = useState(false);
  const [displayCreateUnitModal, setDisplayCreateUnitModal] = useState(false);
  const [displayUnitModal, setDisplayUnitModal] = useState(false);
  const [areUnitCardsHidden, setAreUnitCardsHidden] = useState(true);
  const [unitIndexToEdit, setUnitIndexToEdit] = useState(-1);
  const unitsShowHideId = useId();

  const { mutateAsync: deleteFloorPlan, isPending: isDeletingFloorPlan } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanDeleteV2WebApiHandler_delete,
    skipToast: true, // Handle toast manually based on validation
    meta: {
      invalidates: ['mf-costsAndFees'],
      context: { api: 'RentalManagerUserPropertyFloorPlanDeleteV2WebApiHandler_delete' },
    },
  });
  const { deleteFeedFloorPlan, isPending: isDeletingFeedFloorPlan } = useDeleteFeedFloorPlan(listingAlias);

  const { mutateAsync: updateActive } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_active,
    skipToast: true,
    // This endpoint is shared with floor plans, but here floorPlanId carries a unit id.
    applyToCache: ({ postData }) => [setUnitField(postData.floorPlanId, 'active', Boolean(postData.active))],
    meta: { context: { api: 'RentalManagerUserPropertyFloorPlanEditV2WebApiHandler_active' } },
  });

  const availableText = useMemo(() => {
    let availableText = 'Floor plan available';

    if (floorPlan.units && floorPlan.units.length > 0) {
      let availableUnitCount = 0;
      let availableUnitSoonCount = 0;
      const now = new Date().toISOString().substring(0, 10);

      for (let i = 0; i < floorPlan.units.length; i++) {
        const unit = floorPlan.units[i];
        if (unit.dateAvailable && unit.dateAvailable > now) {
          availableUnitSoonCount++;
        } else if (unit.active) {
          availableUnitCount++;
        }
      }

      const availableUnitArrayText = [];
      if (availableUnitCount > 0) {
        const availableUnitText = `${stringHelper.pluralize(availableUnitCount, 'unit', 'units')} available`;
        availableUnitArrayText.push(availableUnitText);
      }
      if (availableUnitSoonCount > 0) {
        const availableUnitSoonText = `${stringHelper.pluralize(
          availableUnitSoonCount,
          'unit',
          'units',
        )} available soon`;
        availableUnitArrayText.push(availableUnitSoonText);
      }
      if (availableUnitArrayText.length > 0) {
        availableText = availableUnitArrayText.join(', ');
      }
    }

    return availableText;
  }, [floorPlan]);

  const isMissingRequiredInfo = typeCheckUtils.isNumeric(floorPlanIndex)
    ? !floorPlan.name ||
      !(floorPlan.numBedrooms || floorPlan.numBedrooms === 0) ||
      !(floorPlan.numBathrooms || floorPlan.numBathrooms === 0)
    : false;

  const updateStatusMessage = (message: string, status: ToastRootPropsInterface['tone'] = 'success') =>
    enqueueToast(<Toast tone={status}>{message}</Toast>);

  const handleAddUnitClick = () => {
    const { name, numBedrooms, numBathrooms } = floorPlan;
    const canAddUnit = name && (numBedrooms || numBedrooms === 0) && numBathrooms;

    if (!canAddUnit) {
      updateStatusMessage('Add floor plan name, beds, and baths before adding unit', 'critical');
      return;
    }

    setUnitIndexToEdit(-1);
    setDisplayCreateUnitModal(true);
  };

  const handleEditUnitClick = (uIndex: number) => {
    setUnitIndexToEdit(uIndex);
    setDisplayUnitModal(true);
  };

  const handleFloorPlanEditClick = () => {
    setDisplayFloorPlanModal(true);
  };

  const handleToggleActive = async (newActive: boolean, unitIndex: number) => {
    let errorMessage = '';
    if (newActive === false) {
      const canDeactivate = propertyUtils.getActiveFloorPlanOrUnitCount(floorplans) > 1;

      if (!canDeactivate) {
        errorMessage = 'At least one active floor plan is required';
      }
    } else {
      const unit = floorPlan.units?.[unitIndex];
      if (!unit?.name || !unit?.lowPrice) {
        errorMessage = 'Floor plan must be completed to activate';
      }
    }

    if (errorMessage) {
      updateStatusMessage(errorMessage, 'critical');
      return;
    }

    const unitId = floorPlan.units?.[unitIndex]?.id;

    if (!unitId) {
      logError({
        errorType: 'UnitIdMissingError',
        error: new Error('Missing unitId'),
        context: `SavedFloorPlanCard.handleToggleActive: listingAlias: ${listingAlias}, unitIndex: ${unitIndex}, floorPlanIndex: ${floorPlanIndex}`,
      });
      updateStatusMessage('Unit couldn’t be updated. Refresh and try again.', 'critical');
      return;
    }

    try {
      await updateActive({
        postData: {
          floorPlanId: unitId,
          active: newActive,
        },
      });
      updateStatusMessage('Unit updated', 'success');
    } catch (error) {
      updateStatusMessage(
        (error as DatarouterError)?.message || 'Unit couldn’t be updated. Refresh and try again.',
        'critical',
      );
    }
  };

  const handleFloorPlanDeleteClick = async () => {
    const globalActiveCount = propertyUtils.getActiveFloorPlanOrUnitCount(floorplans);

    let isLastActiveFloorplan = true;

    if (floorPlan.units && floorPlan.units.length > 0) {
      const activeUnitsCount = floorPlan.units.reduce((acc, unit) => acc + (unit.active ? 1 : 0), 0);
      isLastActiveFloorplan = activeUnitsCount > 0 && globalActiveCount === activeUnitsCount;
    } else {
      isLastActiveFloorplan = Boolean(floorPlan.active && globalActiveCount === 1);
    }

    // Feed listings get their inventory from the feed, so a floor plan created in Rental Manager can always be removed.
    if (isLastActiveFloorplan && !isFeedFloorPlanDeletable) {
      updateStatusMessage('At least one active floor plan is required', 'critical');
      return;
    }

    try {
      if (isFeedFloorPlanDeletable) {
        if (!floorPlan.feedModelId) {
          logError({
            errorType: 'FeedModelIdMissingError',
            error: new Error('Missing feedModelId'),
            context: `SavedFloorPlanCard.handleFloorPlanDeleteClick: listingAlias: ${listingAlias}, floorPlanIndex: ${floorPlanIndex}`,
          });
          throw new Error('Missing feedModelId');
        }

        await deleteFeedFloorPlan(floorPlan.feedModelId);
        updateStatusMessage('Floor plan deleted', 'success');
        return;
      }

      const { id } = floorPlan;

      if (!id) {
        logError({
          errorType: 'FloorPlanIdMissingError',
          error: new Error('Missing floorPlanId'),
          context: `SavedFloorPlanCard.handleFloorPlanDeleteClick: listingAlias: ${listingAlias}, floorPlanIndex: ${floorPlanIndex}`,
        });
        throw new Error('Missing floorPlanId');
      }

      await deleteFloorPlan({ postData: { floorPlanId: id } });

      updateStatusMessage('Floor plan deleted', 'success');
    } catch (error) {
      updateStatusMessage(
        (error as DatarouterError)?.message || 'Floor plan couldn’t be removed. Wait and try again.',
        'critical',
      );
    }
  };

  const renderUnitsByAvailability = () =>
    [true, false].map((isActive) => (
      <Box
        key={isActive ? 'available' : 'unavailable'}
        css={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '400' }}
      >
        <Box css={{ flex: '0 0 152px' }}>
          <Text textStyle="body-sm" color="text.subtle">
            {isActive ? 'Available ' : 'Unavailable '}units
          </Text>
        </Box>

        <Box css={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '400' }}>
          {floorPlan.units?.map((unit, uIndex) => {
            const shouldRender = isActive ? unit.active : !unit.active;

            return (
              shouldRender && (
                <TextButton key={`unit-${unit.id}`} onClick={() => handleEditUnitClick(uIndex)}>
                  #{unit.name}
                </TextButton>
              )
            );
          })}
        </Box>
      </Box>
    ));

  const renderUnitCards = () => (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
      <Text textStyle="body-sm" color="text.subtle">
        Total units
      </Text>

      <Box css={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '400' }}>
        {floorPlanIndex !== undefined &&
          floorPlan.units?.map((unit, uIndex) => (
            <Box key={`unit-${unit.id}`} css={{ flex: { lg: '1 0 450px', base: 'auto' } }}>
              <UnitCard
                id={`unit-${floorPlanIndex}-${uIndex}`}
                canEditTourOverwrite={listingDetails?.isFeed ?? false}
                isDisabled={isInputFormDisabled}
                floorPlanIndex={floorPlanIndex}
                unit={unit}
                unitIndex={uIndex}
                handleEditUnit={() => handleEditUnitClick(uIndex)}
                onToggleActive={() => handleToggleActive(!unit.active, uIndex)}
                updateStatusMessage={updateStatusMessage}
              />
            </Box>
          ))}

        {/* NOTE: this is a placeholder to handle last row of flex */}
        {Boolean(floorPlan.units && floorPlan.units.length % 2) && (
          // oxlint-disable-next-line constellation/no-raw-spacing-values -- `width: 450.5px` is past the end of the numeric sizes scale, which stops at `5000` (200px)
          <Box css={{ height: 0, width: '450.5px', flex: { lg: '1 0 450px', base: 'auto' } }} />
        )}
      </Box>
    </Box>
  );

  const renderUnitsSection = () => (
    <Box data-testid="UnitsSection" css={{ display: 'flex', flexDirection: 'column', gap: '400', flex: '1' }}>
      {floorPlan.units && floorPlan.units.length > 0 ? (
        <ShowHide.Root
          id={unitsShowHideId}
          isHidden={areUnitCardsHidden}
          onDisplayChange={() => setAreUnitCardsHidden(!areUnitCardsHidden)}
          renderButton={() => null}
        >
          <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
            {renderUnitsByAvailability()}
            <ShowHide.Button aria-controls={unitsShowHideId} css={{ marginBlockStart: '0', alignSelf: 'flex-start' }}>
              {areUnitCardsHidden ? 'Expand' : 'Collapse'}
            </ShowHide.Button>
            <ShowHide.Hidden>{renderUnitCards()}</ShowHide.Hidden>
          </Box>
        </ShowHide.Root>
      ) : (
        renderUnitsByAvailability()
      )}
    </Box>
  );

  return (
    <div data-testid={`SavedFloorPlanCard-${floorPlanIndex}`}>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
        <Box
          css={{
            display: 'flex',
            flexDirection: 'row',
            gap: '200',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box css={{ display: 'flex', flexDirection: 'column', gap: '200', flex: '1' }}>
            <Box css={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
                {isMissingRequiredInfo && (
                  <Tooltip
                    content="Missing required info"
                    data-test="AccordionIconError"
                    trigger={
                      <IconButton
                        icon={<IconErrorFilled />}
                        title="Missing required info"
                        emphasis="bare"
                        shape="circle"
                        size="sm"
                        css={{
                          color: 'icon.feedback.critical.hero-alt',
                          padding: '0',
                          width: 'icon.sm',
                          height: 'icon.sm',
                        }}
                      />
                    }
                  />
                )}
                <Text textStyle="body-bold">
                  {isMissingRequiredInfo ? 'Incomplete' : <>{floorPlan.name || 'Other units'}</>}
                </Text>
              </Box>

              <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
                {!hideAddButton && (
                  <TextButton disabled={isDeletingFloorPlan || isDeletingFeedFloorPlan} onClick={handleAddUnitClick}>
                    Add unit
                  </TextButton>
                )}
                {floorPlan && typeCheckUtils.isNumeric(floorPlanIndex) && Number(floorPlanIndex) >= 0 && (
                  <TextButton
                    disabled={isDeletingFloorPlan || isDeletingFeedFloorPlan}
                    onClick={handleFloorPlanEditClick}
                  >
                    Edit
                  </TextButton>
                )}
                {!isDeleteHidden && typeCheckUtils.isNumeric(floorPlanIndex) && (
                  <TextButton
                    disabled={isDeletingFloorPlan || isDeletingFeedFloorPlan}
                    onClick={handleFloorPlanDeleteClick}
                  >
                    Delete
                  </TextButton>
                )}
              </Box>
            </Box>

            <Box css={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: '400' }}>
              <Text>
                {floorPlanIndex !== null && typeCheckUtils.isNumeric(floorPlanIndex) && (
                  <>
                    {propertyUtils.getFloorPlanDescription(floorPlan).fullDisplayText}
                    {' | '}
                  </>
                )}
                {/* NOTE: ZRM-10234: very first floor plan must be `available` be default */}
                <Text
                  css={{
                    color: floorPlan.active || floorplans.length <= 1 ? 'text.feedback.success.hero' : 'text.subtle',
                  }}
                >
                  {floorPlan.active || floorplans.length <= 1 ? availableText : 'Unavailable'}
                </Text>
              </Text>
            </Box>

            {typeCheckUtils.isNumeric(floorPlanIndex) && (
              <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
                <Icon
                  size="md"
                  css={{ color: !floorPlan?.layout?.url ? 'icon.muted' : undefined }}
                  render={<IconFloorPlanFilled />}
                />
                <Icon
                  size="md"
                  css={{ color: !floorPlan?.floorPlanPhotos?.length ? 'icon.muted' : undefined }}
                  render={<IconPhotosFilled />}
                />
                <Icon
                  size="md"
                  css={{
                    color: !floorplanHasTours ? 'icon.muted' : undefined,
                  }}
                  render={<IconPanorama360Filled />}
                  title="3D Tour"
                />
              </Box>
            )}
          </Box>
        </Box>

        <Box css={{ display: 'flex', flexDirection: 'column', gap: '200', flex: '1' }}>{renderUnitsSection()}</Box>
      </Box>

      {displayUnitModal && (
        <UnitModal
          onClose={() => setDisplayUnitModal(false)}
          floorPlanIndex={floorPlanIndex}
          unitIndexToEdit={unitIndexToEdit}
        />
      )}

      {displayCreateUnitModal && floorPlanIndex !== undefined && (
        <CreateUnitModal
          onClose={() => setDisplayCreateUnitModal(false)}
          floorPlanIndex={floorPlanIndex}
          onSuccess={handleEditUnitClick}
        />
      )}

      {Boolean(
        displayFloorPlanModal && floorPlan && typeCheckUtils.isNumeric(floorPlanIndex) && Number(floorPlanIndex) >= 0,
      ) && <FloorPlanModal floorPlanIndex={Number(floorPlanIndex)} onClose={() => setDisplayFloorPlanModal(false)} />}
    </div>
  );
};

export default SavedFloorPlanCard;
